import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { chunk, filter, isNil, isUndefined } from "lodash";
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CircylCrmContext, useApi } from "../hooks/useApi";
import { TimeSheet } from "../types/timesheet";
import TimesheetFilters from "./TimesheetFilters";
import TimesheetRow from "./TimesheetRow";

interface Props {
  type: "outstanding" | "recently approved" | "history";
  onTimeSheetAmend: (timesheet: TimeSheet) => void;
  setAlertOpen?: Dispatch<SetStateAction<boolean>>;
  setAlertMessage?: Dispatch<SetStateAction<string>>;
  onDataLoaded?: (total: number) => void;
  isEnabled: boolean;
}

const TimesheetTableAll: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const {
    type,
    onTimeSheetAmend,
    setAlertOpen,
    setAlertMessage,
    onDataLoaded,
    isEnabled,
  } = props;
  const { buttonsLocked, setButtonsLocked } = useContext(CircylCrmContext);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [page, setPage] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<
    {
      fieldName:
        | "candidate"
        | "bookingContact"
        | "jobTitle"
        | "jobDescription"
        | "location"
        | "client"
        | "weekEnding";
      value: string;
    }[]
  >();
  const { data: timesheets, isLoading } = useApi<TimeSheet[]>(
    "timesheets",
    {
      status:
        type === "outstanding"
          ? [0, 2, 3]
          : type === "recently approved"
          ? [1]
          : [4],
      // search: "",
    },
    undefined,
    isEnabled
  );
  const [approvedRow, setApprovedRow] = useState<TimeSheet | null>(null);
  const [total, setTotal] = useState<number>(0);

  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isNil(timesheets?.paging?.totalCount)) {
      if (onDataLoaded) onDataLoaded(timesheets?.paging?.totalCount);
    }
  }, [isLoading, onDataLoaded, timesheets?.paging?.totalCount]);

  const handleApprove = (timesheet: TimeSheet) => {
    setApprovedRow(timesheet);
    setAlertOpen!(true);
    setAlertMessage!(t("alerts.approveTimesheet"));
  };

  const { t } = useTranslation();

  const timesheetFilter = useMemo(() => {
    if (isUndefined(timesheets?.results)) {
      return [];
    }
    let results: TimeSheet[] = timesheets?.results;
    if (searchFilter) {
      // weekending
      const weekEnding = searchFilter.find(
        (sf) => sf.fieldName === "weekEnding"
      )?.value;
      if (weekEnding) {
        results = filter(
          results,
          (t) => format(parseISO(t.weekEnding), "dd/MM/yyyy") === weekEnding
        );
      }

      // candidate.
      const candidate = searchFilter.find(
        (sf) => sf.fieldName === "candidate"
      )?.value;
      if (candidate) {
        results = filter(results, (t) => t.candidate === candidate);
      }

      // booking contact.
      const bookingContact = searchFilter.find(
        (sf) => sf.fieldName === "bookingContact"
      )?.value;
      if (bookingContact) {
        results = filter(results, (t) => t.bookingContact === bookingContact);
      }

      // job title.
      const jobTitle = searchFilter.find(
        (sf) => sf.fieldName === "jobTitle"
      )?.value;
      if (jobTitle) {
        results = filter(results, (t) => t.jobTitle === jobTitle);
      }

      // job title.
      const jobDescription = searchFilter.find(
        (sf) => sf.fieldName === "jobDescription"
      )?.value;
      if (jobDescription) {
        results = filter(results, (t) => t.jobDescription === jobDescription);
      }

      // job title.
      const location = searchFilter.find(
        (sf) => sf.fieldName === "location"
      )?.value;
      if (location) {
        results = filter(results, (t) => t.location === location);
      }
    }
    setTotal(results.length);
    return chunk(results, rowsPerPage)[page] || [];
  }, [page, rowsPerPage, searchFilter, timesheets?.results]);

  const count = timesheets?.paging?.totalCount ?? 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const queryClient = useQueryClient();

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    queryClient.invalidateQueries({
      queryKey: ["timesheets"],
    });
  };

  useEffect(() => {
    setButtonsLocked(false);
  }, [setButtonsLocked, timesheets]);

  return (
    <>
      <TimesheetFilters
        type={type}
        onApplyFilters={(filters) => {
          const searchObj: {
            fieldName:
              | "candidate"
              | "bookingContact"
              | "jobTitle"
              | "jobDescription"
              | "location"
              | "client"
              | "weekEnding";
            value: string;
          }[] = [];
          if (filters.candidate) {
            searchObj.push({
              fieldName: "candidate",
              value: filters.candidate,
            });
          }
          if (filters.bookingContact) {
            searchObj.push({
              fieldName: "bookingContact",
              value: filters.bookingContact,
            });
          }
          if (filters.jobTitle) {
            searchObj.push({
              fieldName: "jobTitle",
              value: filters.jobTitle,
            });
          }
          if (filters.jobDescription) {
            searchObj.push({
              fieldName: "jobDescription",
              value: filters.jobDescription,
            });
          }
          if (filters.location) {
            searchObj.push({
              fieldName: "location",
              value: filters.location,
            });
          }
          if (filters.client) {
            searchObj.push({
              fieldName: "client",
              value: filters.client,
            });
          }
          if (filters.weekEnding) {
            searchObj.push({
              fieldName: "weekEnding",
              value: filters.weekEnding,
            });
          }
          setPage(0);
          setSearchFilter(searchObj);
        }}
        isEnabled={
          timesheets?.results && timesheets.results?.length > 0 ? true : false
        }
        defaultFilters={timesheets?.filters}
      />
      {!isLoading ? (
        <>
          {timesheetFilter && timesheetFilter.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {location.pathname === "/history" && (
                        <>
                          <TableCell> {t("tsTable.dateApproved")} </TableCell>
                          <TableCell> {t("tsTable.approver")} </TableCell>
                        </>
                      )}
                      <TableCell sx={{ width: "15%" }}>
                        {t("tsTable.weekEnding")}*
                      </TableCell>
                      <TableCell sx={{ width: "25%" }}>
                        {t("tsTable.candidate")}
                      </TableCell>
                      <TableCell sx={{ width: "40%" }}>
                        {t("tsTable.timesheet")}
                      </TableCell>
                      <TableCell sx={{ width: "15%" }}>
                        {t("tsTable.actions")}
                      </TableCell>
                      {/* <TableCell sx={{ width: "5%" }}> {""} </TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timesheetFilter?.map((timesheet: TimeSheet) => (
                      <TimesheetRow
                        key={timesheet.id}
                        timesheet={timesheet}
                        onApprove={handleApprove}
                        isApproved={timesheet === approvedRow}
                        onAmendClick={(timesheet) =>
                          onTimeSheetAmend(timesheet)
                        }
                      />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10]}
                        colSpan={location.pathname === "/history" ? 7 : 5}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={count <= 0 ? 0 : page}
                        slotProps={{
                          select: {
                            inputProps: {
                              "aria-label": "rows per page",
                            },
                            native: true,
                          },
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                        labelDisplayedRows={({ from, to, count }) =>
                          t("tsTable.paginationText", { from, to, count })
                        }
                        disabled={buttonsLocked}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography variant="body1">
                {t("adminTable.noResults")}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ pt: 4, pb: 4 }} display="flex" justifyContent="center">
          <CircularProgress size={"50px"} />
        </Box>
      )}
    </>
  );
};

export default TimesheetTableAll;
